import { useIntl } from "react-intl";

import ArrowLeftIcon from "@atlaskit/icon/core/arrow-left";
import { LinkMenuItem } from "@atlaskit/navigation-system/side-nav-items/link-menu-item";
import { MenuList } from "@atlaskit/navigation-system/side-nav-items/menu-list";
import { Flex, Text, xcss, Stack } from "@atlaskit/primitives-next";
import Skeleton from "@atlaskit/skeleton";

import { useFastshiftSelector } from "@/components/fastshift/src/state/hook";
import { Picture } from "@/components/wpl/image/src";

import { useRoutes } from "../../../hooks";
import { usePageLayoutAnalytics } from "../hooks";
import { messages } from "../messages";

const selectedCustomerStyles = xcss({
    paddingInlineStart: "space.100",
    paddingBlockEnd: "space.150",
    alignItems: "center",
    gap: "space.100"
});

const selectedCustomerTextStyles = xcss({
    flex: 1
});

const CustomerDomainDisplay = () => {
    const { formatMessage } = useIntl();

    // use the customer domain id to get the customer name
    const { data: customerDomain, pending: isLoadingCustomerDomain } =
        useFastshiftSelector((state) => state.customerDomain);

    // return skeleton if pending refresh or there is no data available
    if (!customerDomain || isLoadingCustomerDomain) {
        return (
            <Flex xcss={selectedCustomerStyles}>
                <Skeleton
                    height="36px"
                    width="36px"
                    borderRadius="border.radius"
                    testId="customer-domain-logo-skeleton"
                    isShimmering
                />
                <Stack xcss={selectedCustomerTextStyles} space="space.100">
                    <Skeleton
                        height="16px"
                        width="100%"
                        testId="customer-name-skeleton"
                        isShimmering
                    />
                    <Skeleton
                        height="16px"
                        width="100%"
                        testId="customer-domain-skeleton"
                        isShimmering
                    />
                </Stack>
            </Flex>
        );
    }

    return (
        <Flex xcss={selectedCustomerStyles}>
            {customerDomain.customerLogoUrl ? (
                <Picture
                    image={{
                        src: customerDomain.customerLogoUrl,
                        alt: formatMessage(messages.logoAltText, {
                            name: customerDomain.customerName
                        })
                    }}
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "4px"
                    }}
                />
            ) : (
                <Skeleton
                    height="36px"
                    width="36px"
                    borderRadius="4px"
                    testId="customer-domain-logo-skeleton"
                />
            )}
            <Stack xcss={selectedCustomerTextStyles}>
                <Text size="large">{customerDomain.customerName}</Text>
                <Text color="color.text.subtlest">
                    {customerDomain.customerDomain}
                </Text>
            </Stack>
        </Flex>
    );
};

export const DomainScopedNavHeader = ({
    isOnDomainScopedPage
}: {
    isOnDomainScopedPage: boolean;
}) => {
    const { formatMessage } = useIntl();
    const { paths } = useRoutes();
    const { sendCustomerSelectionBackButtonClickEvent } =
        usePageLayoutAnalytics();

    if (!isOnDomainScopedPage) {
        return null;
    }

    return (
        <Stack space="space.100">
            <MenuList>
                <LinkMenuItem
                    href={paths.selection}
                    elemBefore={
                        <ArrowLeftIcon
                            label=""
                            color="currentColor"
                            spacing="spacious"
                        />
                    }
                    onClick={() => {
                        sendCustomerSelectionBackButtonClickEvent(
                            paths.selection
                        );
                    }}
                >
                    {formatMessage(messages.customersBackLabel)}
                </LinkMenuItem>
            </MenuList>

            {/* Nav Header - Customer Domain Name - only when scoped to Domain*/}
            <CustomerDomainDisplay />
        </Stack>
    );
};
